import React, { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { Button } from "~/shared/components/MuiComponents.jsx";

import AccessTime from "@mui/icons-material/AccessTime";
import Check from "@mui/icons-material/Check";
import LocalPharmacy from "@mui/icons-material/LocalPharmacy";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import Phone from "@mui/icons-material/Phone";
import VerifiedUser from "@mui/icons-material/VerifiedUser";
import Warning from "@mui/icons-material/Warning";
import MuiCheckbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Modal from "@mui/material/Modal";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { useTheme } from "@mui/material/styles";
import MuiTextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  kickBlue,
  kickFreeGreen,
  kickLightBlue,
  kickLightPink,
  kickNavy,
  kickPink,
  kickRed,
} from "~/shared/styles/baseStyles.js";
import {
  AutocompleteUSStates,
  usStatesList,
} from "~/shared/widgets/autocompleteUSStates.jsx";
import TestimonialsCarousel from "~/shared/widgets/TestimonialsCarousel.jsx";
import TrustBox from "~/shared/widgets/TrustBox.jsx";

import {
  CHOCO_CHILL_MINT_FLAVOR,
  CITRUS_ZEN_FLAVOR,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  // Sleep
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  FRUIT_PUNCH_FLAVOR,
  // Atenolol + Scopolamine
  ORDER_TYPE_ATE_SCO_1_PACK,
  ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
  ORDER_TYPE_ATE_SCO_2_PACK,
  ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_2_PACK_REFILL,
  ORDER_TYPE_PROP_1_PACK,
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_REFILL,
  // BB
  ORDER_TYPE_PROP_2_PACK,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_REFILL,
  ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
  PRICE_SLEEP_MEMBERSHIP_MONTHLY,
  PRICE_SLEEP_MEMBERSHIP_MONTHLY_PER_DAY,
  PRICE_SLEEP_MEMBERSHIP_QUARTERLY,
  PRICE_SLEEP_MEMBERSHIP_QUARTERLY_PER_DAY,
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  // Monthly
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY_PER_DAY,
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
  // Quarterly
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY_PER_DAY,
  SHIPPING_METHOD_STANDARD,
  SHIPPING_METHOD_TWO_DAY,
  SHIPPING_METHOD_TWO_DAY_PRICE,
  SPEARMINT_FLAVOR,
  TANGERINE_FLAVOR,
} from "~/shared/sharedConstants";

import {
  getOrderItemAddonsList,
  getOrderItemSubcategory,
  getPostCheckoutInfo,
  getSMSConsent,
  getVisitProduct,
  setConsultationTypeOrderItem,
  setFollowUpVisitFlag,
  setIsPickup,
  setShipmentMethod as setShipmentMethodCookie,
  setVisitID,
  setVisitProduct,
} from "~/visits/utils/visitCookies.js";

import { isRefillCheckoutSession } from "~/visits/utils/visitUtils";

import {
  isConsultationTypeInsomnia,
  isMailOrderBBOrderType,
  isPharmacyPickupOrderType,
  isPharmacyPickupSleepOrderType,
  supportedUSStates,
  supportedUSStatesForCompoundedMeds,
} from "~/shared/utils/sharedUtils";

import {
  originalAddressReadable,
  suggestedAddressReadable,
} from "~/shared/addressValidation";
import {
  canSelectShippingMethod,
  getProductPrice,
  getSelectedSleepMeds,
  getShippingPrice,
  isSleepMembership,
  medicationPrice,
  onlineVisitFee,
  orderTotalPriceWithCouponDiscount,
  productName,
  shouldShowCouponDiscount,
  showPromoDiscount,
} from "./paymentHelpers";

import { TextField, TextMaskPhone } from "../shared.jsx";

import imgChocoChillMint from "~assets/images/flavors/choco-chill.png";
import imgCitrusZen from "~assets/images/flavors/citrus-zen.png";
import imgSamplePackAllFlavors from "~assets/images/flavors/sample-pack-all-flavors.png";
import imgSpearmint from "~assets/images/flavors/spearmint-flavor.png";
import imgTangerine from "~assets/images/flavors/tangerine-flavor.png";
import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";
import imgOnePack from "~assets/images/visits/one-pack.png";
import imgSleepTrial from "~assets/images/visits/sleep-bottles.jpg";
import imgTwoPack from "~assets/images/visits/two-pack.png";

import { VISIT_MAX_WIDTH } from "~/visits/styles/consultationStyles.js";

const ONE_MONTH_RECURRING_OPTION = "oneMonthRecurring";
const THREE_MONTH_RECURRING_OPTION = "threeMonthRecurring";
const checkoutOptionHeadingStyle = { margin: "0 0 12px" };

// The "Ships by <date>" line under the delivery options. It sits
// outside the radio labels, so it sets its own font instead of inheriting the
// page body's. The truck lines up with the radio circles and the text with the
// option labels: -2px is FormControlLabel's -11px margin plus Radio's 9px
// padding, the 9px gap is Radio's padding again, and the icon keeps SvgIcon's
// default size, which is also the radio circle's.
const shipByDateStyle = {
  display: "flex",
  alignItems: "center",
  gap: "9px",
  margin: "6px 0 0 -2px",
  fontFamily: '"BasisGrotesquePro-Regular", "Helvetica", "Arial", sans-serif',
  fontSize: "14px",
  fontWeight: "normal",
  lineHeight: 1.4,
  color: kickNavy,
};

export function ProductImage({
  consultationType,
  classes,
  productType,
  subcategory,
  imgStyle = {},
  checkoutSessionID,
}) {
  const postCheckoutData = getPostCheckoutInfo(checkoutSessionID);
  productType =
    productType ||
    getVisitProduct(checkoutSessionID) ||
    postCheckoutData?.product ||
    "";
  subcategory =
    subcategory ||
    getOrderItemSubcategory(consultationType, checkoutSessionID) ||
    postCheckoutData?.orderItems?.[consultationType]?.subcategory ||
    "";
  let imgFile;

  if (productType.includes("visit-only")) {
    return (
      <div
        style={{
          color: kickBlue,
          margin: "0 16px -4px 10px",
        }}
      >
        <LocalPharmacy style={{ fontSize: "36px" }} />
      </div>
    );
  }

  switch (consultationType) {
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
      imgFile = imgSleepTrial;
      break;

    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
      // no product select or visit only, don't show matchbox image
      if (!productType) {
        return null;
      }

      switch (productType) {
        case ORDER_TYPE_PROP_1_PACK:
        case ORDER_TYPE_PROP_1_PACK_REFILL:
        case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
          imgFile = imgOnePack;
          break;

        case ORDER_TYPE_PROP_2_PACK:
        case ORDER_TYPE_PROP_2_PACK_REFILL:
        case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
          imgFile = imgTwoPack;
          break;

        case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
          imgFile = imgSamplePackAllFlavors;
          break;

        case ORDER_TYPE_PROP_1_PACK_FLAVORED:
        case ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL:
        case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
        case ORDER_TYPE_PROP_2_PACK_FLAVORED:
        case ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL:
        case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
        case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED: {
          switch (subcategory) {
            case TANGERINE_FLAVOR:
              imgFile = imgTangerine;
              break;
            case SPEARMINT_FLAVOR:
              imgFile = imgSpearmint;
              break;
            case FRUIT_PUNCH_FLAVOR:
              imgFile = imgFruitPunch;
              break;
            case CHOCO_CHILL_MINT_FLAVOR:
              imgFile = imgChocoChillMint;
              break;
            case CITRUS_ZEN_FLAVOR:
              imgFile = imgCitrusZen;
              break;
            default:
              imgFile = imgTwoPack;
              break;
          }
          break;
        }

        case ORDER_TYPE_ATE_SCO_1_PACK:
        case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
        case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
        case ORDER_TYPE_ATE_SCO_2_PACK:
        case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
        case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP: {
          // Use existing flavor images as fallbacks for ate-sco
          switch (subcategory) {
            case CHOCO_CHILL_MINT_FLAVOR:
              imgFile = imgChocoChillMint;
              break;
            case CITRUS_ZEN_FLAVOR:
              imgFile = imgCitrusZen;
              break;
            default:
              imgFile = productType.includes("2-pack")
                ? imgTwoPack
                : imgOnePack;
              break;
          }
          break;
        }

        default:
          imgFile = imgTwoPack;
          break;
      }
      break;

    case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY:
    case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP:
      switch (productType) {
        case ORDER_TYPE_ATE_SCO_1_PACK:
        case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
        case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
        case ORDER_TYPE_ATE_SCO_2_PACK:
        case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
        case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP: {
          // Use existing flavor images as fallbacks for ate-sco
          switch (subcategory) {
            case CHOCO_CHILL_MINT_FLAVOR:
              imgFile = imgChocoChillMint;
              break;
            case CITRUS_ZEN_FLAVOR:
              imgFile = imgCitrusZen;
              break;
            default:
              imgFile = productType.includes("2-pack")
                ? imgTwoPack
                : imgOnePack;
              break;
          }
          break;
        }
      }
      break;
  }

  return (
    <div
      style={{
        marginRight: isMailOrderBBOrderType(getVisitProduct(checkoutSessionID))
          ? "6px"
          : "20px",
        marginLeft:
          getVisitProduct(checkoutSessionID) === ORDER_TYPE_PROP_1_PACK
            ? "-20px"
            : "0",
      }}
    >
      <img
        className={classes.orderDetailsProductImage}
        src={imgFile}
        alt="Product Image"
        style={imgStyle}
      />
    </div>
  );
}

export function DoctorGuaranteeSection({ classes, style }) {
  return (
    <div style={style}>
      <div className={classes.paymentGroupPaymentDisclaimer}>
        <p
          style={{
            margin: "0 auto",
            fontSize: "18px",
            width: "100%",
            color: kickBlue,
            fontFamily: "Basis-GrotesquePro-Bold",
            display: "flex",
            alignItems: "center",
          }}
        >
          <VerifiedUser style={{ fontSize: "15px", marginRight: "6px" }} />
          <span>Kick's Doctor Guarantee</span>
        </p>
        <p style={{ color: kickNavy, fontSize: "15px" }}>
          Risk-free &mdash; you&rsquo;ll only be charged if a doctor approves a
          prescription for you
        </p>
      </div>
    </div>
  );
}

export function ShippingAvailabilityNotice({ usState }) {
  const destination = usStatesList.find(
    (state) => state.abbreviation === usState,
  );
  if (!destination || supportedUSStates.includes(usState)) return null;

  const stateName = (state) => state.name.replace(/\s+\([A-Z]{2}\)$/, "");
  return (
    <div
      style={{
        backgroundColor: kickLightPink,
        borderRadius: "8px",
        color: kickNavy,
        marginBottom: "16px",
        padding: "16px",
      }}
    >
      <div role="status">
        <p style={{ margin: "0 0 8px" }}>
          <strong>We’re not available in {stateName(destination)}</strong>
        </p>
        <p style={{ margin: 0 }}>
          Please choose a shipping address in a state we serve to continue.
        </p>
      </div>
      <details style={{ marginTop: "8px" }}>
        <summary
          style={{
            color: kickBlue,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          View available states
        </summary>
        <ul
          style={{
            columns: "140px 2",
            fontSize: "14px",
            lineHeight: 1.6,
            marginBottom: 0,
            paddingLeft: "20px",
          }}
        >
          {usStatesList
            .filter((state) => supportedUSStates.includes(state.abbreviation))
            .map((state) => (
              <li key={state.abbreviation}>{stateName(state)}</li>
            ))}
        </ul>
      </details>
    </div>
  );
}

export function ShippingRadios({
  classes,
  setTotalPrice,
  shipMethod,
  setShipMethod,
  checkoutSessionID,
  consultationType,
  shipByDate = "",
}) {
  const product = getVisitProduct(checkoutSessionID);
  return (
    canSelectShippingMethod(product, consultationType) && (
      <div style={{ marginBottom: "30px" }}>
        <h3 className={classes.subtitle} style={checkoutOptionHeadingStyle}>
          Delivery Speed
        </h3>
        <FormControl
          variant="outlined"
          className={classes.formControl}
          style={{ width: "100%" }}
        >
          <RadioGroup
            value={shipMethod || SHIPPING_METHOD_STANDARD}
            onChange={(e) => {
              setShipMethod(e.target.value);
              setShipmentMethodCookie(e.target.value, checkoutSessionID);
              setTotalPrice(
                orderTotalPriceWithCouponDiscount(true, checkoutSessionID),
              );
            }}
          >
            <FormControlLabel
              value={SHIPPING_METHOD_TWO_DAY}
              control={<Radio />}
              label={
                <span>
                  {`FedEx Priority (2-3 days) - $${SHIPPING_METHOD_TWO_DAY_PRICE}.00`}
                  {consultationType ===
                    CONSULTATION_TYPE_ABBREVIATION_INSOMNIA && (
                    <span
                      style={{
                        display: "block",
                        marginTop: "2px",
                        fontSize: "13px",
                        fontWeight: "normal",
                        lineHeight: 1.4,
                        color: "#666",
                      }}
                    >
                      Non-recurring – applies to this order only
                    </span>
                  )}
                </span>
              }
            />
            <FormControlLabel
              value={SHIPPING_METHOD_STANDARD}
              control={<Radio color="secondary" />}
              label={
                <>
                  USPS Standard (5-9 days) -{" "}
                  <span style={{ color: kickFreeGreen }}>FREE</span>
                </>
              }
            />
          </RadioGroup>
        </FormControl>
        {shipByDate ? (
          <p data-testid="ship-by-date" style={shipByDateStyle}>
            <LocalShippingOutlined style={{ color: kickBlue }} />
            <span>
              Ships by{" "}
              <strong style={{ fontWeight: "bold" }}>{shipByDate}</strong>
            </span>
          </p>
        ) : null}
      </div>
    )
  );
}

// SocialProof shows the Trustpilot rating plus a per-vertical testimonials carousel.
// `consultationType` is the route param (pac/paf/gac/gaf/inc/inf); every other prop
// is forwarded to the wrapper <div>, so destructure it here to keep it off the DOM.
export function SocialProof({ consultationType, ...divProps }) {
  // Dynamic total patients served
  // const [totalPatientsServed, setTotalPatientsServed] = useState(0);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     const client = KickClient.newAuthenticatedClient();
  //     const totalPatientsServed = await client.get("/total-patients-served");
  //     setTotalPatientsServed(totalPatientsServed.data);
  //   };
  //   if (!totalPatientsServed) {
  //     fetchData().catch(console.error);
  //   }
  // });
  return (
    <div {...divProps}>
      <div
        style={{
          marginTop: "20px",
          fontFamily: "Spectral-SemiBold",
          fontSize: "20px",
          // fontWeight: "bold",
        }}
      >
        {/* {totalPatientsServed} patients served */}
        Over <span>180,000+</span> Satisfied Patients
      </div>
      <div style={{ marginTop: "20px", position: "relative" }}>
        <div
          style={{
            height: "100%",
            width: "100%",
            backgroundColor: "transparent",
            position: "absolute",
            zIndex: 100,
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        />
        <TrustBox style={{ marginBottom: "20px" }} />
      </div>
      {/* Always rendered; the quotes are picked per vertical from consultationType. */}
      <div style={{ marginBottom: "30px" }}>
        <TestimonialsCarousel consultationType={consultationType} />
      </div>
    </div>
  );
}

export function ProductDescription({
  consultationType,
  classes,
  center,
  postCheckout = false,
  checkoutSessionID,
}) {
  let visitProduct = getVisitProduct(checkoutSessionID);

  let productTitle = "";
  if (postCheckout) {
    const postCheckoutData = getPostCheckoutInfo(checkoutSessionID);
    visitProduct = postCheckoutData?.productSlug || visitProduct;
    productTitle = productName(
      visitProduct,
      postCheckoutData?.orderItems?.[consultationType]?.subcategory,
      checkoutSessionID,
    );
  } else {
    productTitle = productName(
      visitProduct,
      getOrderItemSubcategory(consultationType, checkoutSessionID),
      checkoutSessionID,
    );
  }
  let productSubtitle = "Includes doctor's visit fee";
  productSubtitle += isPharmacyPickupOrderType(visitProduct)
    ? ". You'll pay the pharmacy directly for medication."
    : " + medication";
  return (
    <div
      style={{
        display: "flex",
        borderBottom: "1px solid #ddd",
        marginBottom: "20px",
        paddingBottom: "16px",
        alignItems: "center",
        width: "100%",
        maxWidth: VISIT_MAX_WIDTH,
        justifyContent: center ? "center" : "flex-start",
      }}
    >
      <ProductImage
        consultationType={consultationType}
        classes={classes}
        productType={visitProduct}
        subcategory={getOrderItemSubcategory(
          consultationType,
          checkoutSessionID,
        )}
        checkoutSessionID={checkoutSessionID}
      />
      <div>
        <div style={{ fontWeight: "bold", fontSize: "16px" }}>
          {productTitle}
        </div>
        {!isConsultationTypeInsomnia(consultationType) && (
          <div style={{ marginTop: "6px" }}>{productSubtitle}</div>
        )}
      </div>
    </div>
  );
}

const orderCostRowStyle = {
  display: "flex",
  justifyContent: "space-between",
};

const orderCostRowRightColumnStyle = {
  textAlign: "right",
  width: "60px",
  marginLeft: "16px",
};

const orderCostRowLeftColumnStyle = {
  textAlign: "left",
  maxWidth: "200px",
};

function ShippingCost({ classes, shipMethod, price }) {
  return (
    <div style={orderCostRowStyle}>
      <p
        className={classes.paymentHeading}
        style={{
          ...orderCostRowLeftColumnStyle,
          color: price === "FREE" ? kickFreeGreen : "",
        }}
      >
        {shipMethod === SHIPPING_METHOD_TWO_DAY
          ? "FedEx Shipping"
          : "USPS Shipping"}
      </p>
      <p
        className={classes.paymentHeading}
        style={{
          ...orderCostRowRightColumnStyle,
          color: price === "FREE" ? kickFreeGreen : "",
        }}
      >
        {price}
      </p>
    </div>
  );
}

// PromoCodeInput component for entering and validating promo codes
export function PromoCodeInput({
  onApplyPromoCode,
  promoCode,
  isValidating,
  errorMessage,
}) {
  const [isExpanded, setIsExpanded] = useState(promoCode?.isValid || false);
  const [inputValue, setInputValue] = useState(promoCode?.code || "");

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setInputValue(value);

    // If a promo code is already applied, clear it when user edits
    if (promoCode?.isValid) {
      onApplyPromoCode(null);
    }
  };

  const handleApply = () => {
    if (inputValue.length >= 1) {
      onApplyPromoCode(inputValue);
    }
  };

  const handleRemove = () => {
    setInputValue("");
    onApplyPromoCode(null);
  };

  if (!isExpanded && !promoCode?.isValid) {
    return (
      <div style={{ marginTop: "12px", marginBottom: "12px" }}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setIsExpanded(true);
          }}
          style={{
            color: kickNavy,
            textDecoration: "underline",
            fontSize: "14px",
          }}
        >
          Have a referral code?
        </a>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "12px", marginBottom: "12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <input
          type="text"
          placeholder="Enter referral code"
          value={inputValue}
          onChange={handleInputChange}
          disabled={isValidating || promoCode?.isValid}
          autoFocus
          style={{
            padding: "8px 12px",
            border: `1px solid ${errorMessage ? "#d32f2f" : "#ddd"}`,
            borderRadius: "4px",
            fontSize: "14px",
            width: "200px",
          }}
        />
        {!promoCode?.isValid && (
          <button
            onClick={handleApply}
            disabled={isValidating || inputValue.length < 1}
            style={{
              padding: "8px 16px",
              backgroundColor: kickBlue,
              color: "white",
              border: "none",
              borderRadius: "4px",
              fontSize: "14px",
              cursor:
                isValidating || inputValue.length < 1
                  ? "not-allowed"
                  : "pointer",
              opacity: isValidating || inputValue.length < 1 ? 0.6 : 1,
            }}
          >
            {isValidating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              "Apply"
            )}
          </button>
        )}
      </div>

      {promoCode?.isValid && (
        <div style={{ marginTop: "8px", color: "#2e7d32", fontSize: "14px" }}>
          Promo code applied
          <button
            onClick={handleRemove}
            style={{
              marginLeft: "8px",
              background: "none",
              border: "none",
              color: kickNavy,
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Remove
          </button>
        </div>
      )}

      {errorMessage && (
        <div style={{ marginTop: "8px", color: "#d32f2f", fontSize: "14px" }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
}

export function OrderCosts({
  classes,
  consultationType,
  shipMethod,
  totalPrice,
  checkoutSessionID,
  promoCode,
  onApplyPromoCode,
  isValidatingPromoCode,
  promoCodeError,
}) {
  function getShipmentPrice() {
    return shipMethod === SHIPPING_METHOD_TWO_DAY
      ? `$${SHIPPING_METHOD_TWO_DAY_PRICE.toFixed(2)}`
      : "FREE";
  }

  return (
    <div>
      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: "4px",
          padding: "24px",
          maxWidth: VISIT_MAX_WIDTH,
        }}
      >
        <ProductDescription
          consultationType={consultationType}
          classes={classes}
          checkoutSessionID={checkoutSessionID}
        />

        <div
          className={classes.paymentOrderSummarySection}
          style={{ display: "block" }}
        >
          <div style={orderCostRowStyle}>
            <p
              className={classes.paymentHeading}
              style={orderCostRowLeftColumnStyle}
            >
              Online Visit Fee
            </p>
            <p
              className={classes.paymentHeading}
              style={orderCostRowRightColumnStyle}
            >
              ${onlineVisitFee(checkoutSessionID)}
            </p>
          </div>
          <div style={orderCostRowStyle}>
            <p
              className={classes.paymentHeading}
              style={orderCostRowLeftColumnStyle}
            >
              Medication
            </p>
            <p
              className={classes.paymentHeading}
              style={orderCostRowRightColumnStyle}
            >
              {medicationPrice(checkoutSessionID)}
            </p>
          </div>

          {getOrderItemAddonsList(checkoutSessionID).map((item) => (
            <div key={item.productSlug + "-product"} style={orderCostRowStyle}>
              <p
                className={classes.paymentHeading}
                style={orderCostRowLeftColumnStyle}
              >
                {productName(
                  item.productSlug,
                  item.subcategory,
                  checkoutSessionID,
                )}
              </p>
              <p
                className={classes.paymentHeading}
                style={orderCostRowRightColumnStyle}
              >
                ${getProductPrice(item.productSlug).toLocaleString() + ".00"}
              </p>
            </div>
          ))}

          {(promoCode?.isValid || shouldShowCouponDiscount()) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <p
                className={
                  classes.paymentHeading + " " + classes.paymentHeadingDiscount
                }
                style={{
                  ...orderCostRowLeftColumnStyle,
                }}
              >
                Discount {promoCode?.isValid && `(${promoCode.code})`}
              </p>
              <p
                className={
                  classes.paymentHeading + " " + classes.paymentHeadingDiscount
                }
                style={{
                  ...orderCostRowRightColumnStyle,
                }}
              >
                {showPromoDiscount(promoCode)}
              </p>
            </div>
          )}

          {!isPharmacyPickupOrderType(getVisitProduct(checkoutSessionID)) && (
            <ShippingCost
              classes={classes}
              shipMethod={shipMethod}
              price={getShipmentPrice()}
            />
          )}

          <PromoCodeInput
            onApplyPromoCode={onApplyPromoCode}
            promoCode={promoCode}
            isValidating={isValidatingPromoCode}
            errorMessage={promoCodeError}
          />

          <div style={orderCostRowStyle}>
            <p className={classes.paymentTotal} style={{ marginTop: "16px" }}>
              Total
            </p>
            <p className={classes.paymentTotal} style={{ marginTop: "16px" }}>
              ${Number(totalPrice).toFixed(2)}
            </p>
          </div>
        </div>
      </div>
      {isSleepMembership(checkoutSessionID) && (
        <div
          style={{
            margin: "10px",
            textAlign: "center",
            fontWeight: "bold",
            maxWidth: VISIT_MAX_WIDTH,
          }}
        >
          Plan renews at end of term. Cancel anytime in your account.
        </div>
      )}
    </div>
  );
}

export function SleepOrderCosts({
  classes,
  consultationType,
  shipMethod,
  totalPrice,
  checkoutSessionID,
  promoCode,
  onApplyPromoCode,
  isValidatingPromoCode,
  promoCodeError,
}) {
  function getShipmentPrice() {
    const shippingPrice = getShippingPrice(checkoutSessionID, promoCode);
    return shippingPrice ? `$${shippingPrice.toFixed(2)}` : "FREE";
  }

  return (
    <div>
      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: "4px",
          padding: "24px",
          maxWidth: VISIT_MAX_WIDTH,
        }}
      >
        <ProductDescription
          consultationType={consultationType}
          classes={classes}
          checkoutSessionID={checkoutSessionID}
        />
        <div className={classes.paymentOrderSummarySection}>
          <div className={classes.paymentOrderSummaryTitles}>
            <p className={classes.paymentHeading}>Unlimited Online Visits</p>
            <p className={classes.paymentHeading}>Medication</p>

            {(promoCode?.isValid || shouldShowCouponDiscount()) && (
              <p
                className={
                  classes.paymentHeading + " " + classes.paymentHeadingDiscount
                }
              >
                Discount {promoCode?.isValid && `(${promoCode.code})`}
              </p>
            )}
          </div>

          <div className={classes.paymentOrderSummaryPrices}>
            <p className={classes.paymentHeading}>
              ${onlineVisitFee(checkoutSessionID)}
            </p>
            <p className={classes.paymentHeading}>
              {isPharmacyPickupOrderType(getVisitProduct(checkoutSessionID))
                ? "Pay At Pharmacy"
                : "INCLUDED"}
            </p>

            {(promoCode?.isValid || shouldShowCouponDiscount()) && (
              <p
                className={
                  classes.paymentHeading + " " + classes.paymentHeadingDiscount
                }
              >
                {showPromoDiscount(promoCode)}
              </p>
            )}
          </div>
        </div>

        {!isPharmacyPickupOrderType(getVisitProduct(checkoutSessionID)) && (
          <ShippingCost
            classes={classes}
            shipMethod={shipMethod}
            price={getShipmentPrice()}
          />
        )}

        <div style={orderCostRowStyle}>
          <p className={classes.paymentTotal} style={{ marginTop: "16px" }}>
            Total
          </p>
          <p className={classes.paymentTotal} style={{ marginTop: "16px" }}>
            ${Number(totalPrice).toFixed(2)}
          </p>
        </div>

        <PromoCodeInput
          onApplyPromoCode={onApplyPromoCode}
          promoCode={promoCode}
          isValidating={isValidatingPromoCode}
          errorMessage={promoCodeError}
        />
      </div>
      {isSleepMembership(checkoutSessionID) && (
        <div
          style={{
            margin: "10px",
            textAlign: "center",
            fontWeight: "bold",
            maxWidth: VISIT_MAX_WIDTH,
          }}
        >
          Plan renews at end of term. Cancel anytime in your account.
        </div>
      )}
    </div>
  );
}

export function ShippingInputs({
  classes,
  errors,
  control,
  address1,
  address2,
  city,
  usState,
  zip,
  phone,
  smsConsent,
  disabled,
}) {
  return (
    <div className={classes.paymentShippingForm}>
      <Controller
        control={control}
        defaultValue={address1}
        name="address1"
        render={({ field }) => (
          <TextField
            disabled={disabled}
            variant="outlined"
            {...field}
            labeltext="Address 1"
            fullWidth
            placeholder="e.g. 123 Main St"
            inputProps={{
              maxLength: 35,
            }}
          />
        )}
      />
      <p style={{ color: "red" }}>{errors.address1?.message}</p>
      <Controller
        control={control}
        defaultValue={address2}
        name="address2"
        render={({ field }) => (
          <TextField
            disabled={disabled}
            variant="outlined"
            {...field}
            labeltext="Address 2"
            fullWidth
            placeholder="e.g. Apt 1"
            inputProps={{
              maxLength: 35,
            }}
          />
        )}
      />
      <p style={{ color: "red" }}>{errors.address2?.message}</p>
      <Controller
        render={({ field }) => (
          <TextField
            disabled={disabled}
            variant="outlined"
            {...field}
            labeltext="City"
            fullWidth
            placeholder="e.g. New York"
          />
        )}
        name="city"
        control={control}
        defaultValue={city}
      />
      <p style={{ color: "red" }}>{errors.city?.message}</p>
      <Controller
        name="usState"
        control={control}
        defaultValue={usState}
        render={({ field: { value, onChange, onBlur } }) => (
          <>
            <label className={classes.label}>State</label>
            <AutocompleteUSStates
              disabled={disabled}
              value={value || null}
              onBlur={onBlur}
              onChange={(e) => {
                if (!e) {
                  onChange(null);
                  return;
                }
                let validatedEvent = { ...e };
                if (validatedEvent.target) {
                  validatedEvent.target.value =
                    validatedEvent?.target?.value?.replace(/[^a-zA-Z]/g, "");
                }
                onChange(validatedEvent);
              }}
              fullWidth
              variant="outlined"
            />
          </>
        )}
      />
      <p style={{ color: "red" }}>{errors.usState?.message}</p>
      <Controller
        render={({ field }) => (
          <TextField
            disabled={disabled}
            variant="outlined"
            {...field}
            fullWidth
            labeltext="Zip code"
            placeholder="e.g. 10001"
            inputProps={{
              maxLength: 5,
            }}
          />
        )}
        name="zip"
        control={control}
        defaultValue={zip}
      />
      <p style={{ color: "red" }}>{errors.zip?.message}</p>
      <Controller
        render={({ field }) => (
          <TextField
            disabled={disabled}
            variant="outlined"
            {...field}
            fullWidth
            labeltext="Phone"
            type="tel"
            placeholder="(123) 456-7890"
            InputProps={{
              inputComponent: TextMaskPhone,
            }}
          />
        )}
        name="phone"
        control={control}
        defaultValue={phone}
      />
      <p style={{ color: "red" }}>{errors.phone?.message}</p>
      {!getSMSConsent() && (
        <Controller
          render={({ field }) => (
            <div style={{ display: "flex", marginTop: "20px" }}>
              <MuiCheckbox
                checked={field.value}
                disabled={disabled}
                {...field}
                checkedIcon={<Check className={classes.checkedIcon} />}
                icon={<Check className={classes.uncheckedIcon} />}
                classes={{
                  checked: classes.checked,
                }}
                style={{
                  padding: 0,
                  marginRight: "6px",
                  opacity: disabled ? 0.5 : 1,
                }}
              />
              <span style={{ color: kickNavy }}>
                Receive order and marketing updates via SMS
              </span>
            </div>
          )}
          name="smsConsent"
          control={control}
          defaultValue={smsConsent}
        />
      )}
    </div>
  );
}

export function StateNotSupportedModal({
  showModal,
  setShowModal,
  consultationType,
  modalData = { unsupportedState: "" },
  checkoutSessionID,
}) {
  let history = useHistory();
  // Shared by the inline link and the button below. A refill session must NOT be sent to
  // select-pharmacy: no pickup refill SKU exists, and the pickup swap there would post a full
  // follow-up SKU against the session's visitID — the patient's ORIGINAL, already-closed visit
  // (a new pickup pharmacy needs a new prescription, which needs a new visit). Route refills
  // into a fresh follow-up funnel session instead (same destination as the refill flow's
  // "make changes"). isRefillCheckoutSession is also null-safe for render sites that don't
  // thread a checkoutSessionID (confirmShippingDetails historically didn't).
  const goToPharmacyPickup = () => {
    setIsPickup(true);
    if (isRefillCheckoutSession(checkoutSessionID)) {
      setVisitID("", checkoutSessionID);
      const newCheckoutSessionID = uuidv4();
      setFollowUpVisitFlag("yes", newCheckoutSessionID);
      history.push(
        `/visits/${CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP}/1?checkoutSessionID=${newCheckoutSessionID}`,
      );
      return;
    }
    // if initial PA visit, go to pharmacy selection
    history.push(
      `/visits/${consultationType}/select-pharmacy?checkoutSessionID=${checkoutSessionID}`,
    );
  };
  return (
    <Modal open={showModal}>
      <div className="address-modal-container">
        <div className="inner-modal-container">
          <p style={{ marginBottom: "20px" }}>
            {`Unfortunately, we're not
            available in ${modalData.unsupportedState} yet. Do you have an address in one of the states below? You can also `}
            <a style={{ cursor: "pointer" }} onClick={goToPharmacyPickup}>
              pick up from a pharmacy in these states.
            </a>
          </p>
          <p style={{ marginBottom: "20px" }}>
            {`We're available in ${supportedUSStates.join(", ")}.`}
          </p>
          <div className="button-container">
            <Button
              onClick={() => setShowModal({})}
              className="button back-button"
            >
              Change Shipping Address
            </Button>
            <Button onClick={goToPharmacyPickup} className="button">
              Pharmacy Pick-Up
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function CompoundedStateNotSupportedModal({
  showModal,
  setShowModal,
  modalData = { unsupportedState: "" },
}) {
  return (
    <Modal open={showModal}>
      <div className="address-modal-container">
        <div className="inner-modal-container">
          <p style={{ marginBottom: "20px" }}>
            {`Unfortunately, we're not
            able to sell compounded medications in ${modalData.unsupportedState} yet. Do you have an address in one of the states below?`}
          </p>
          <p style={{ marginBottom: "20px" }}>
            {`We're available in ${supportedUSStatesForCompoundedMeds.join(
              ", ",
            )}.`}
          </p>
          <div className="button-container">
            <Button
              onClick={() => setShowModal({})}
              className="button back-button"
            >
              Change Shipping Address
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function ConfirmAddressModal({
  showModal,
  setShowModal,
  modalData = { originalAddress: {} },
  submit,
}) {
  const [selectedIdx, setSelectedIdx] = useState("1");

  return (
    <Modal open={showModal}>
      <div className="address-modal-container">
        <div className="inner-modal-container">
          <h3>Confirm your address</h3>
          <p style={{ marginBottom: "20px" }}>Review the recommended changes</p>

          <RadioGroup
            value={selectedIdx}
            onChange={(e) => setSelectedIdx(e.target.value)}
          >
            <div
              onClick={() => setSelectedIdx("0")}
              className={`address-section${
                selectedIdx === "0" ? " selected" : ""
              }`}
            >
              <div>
                <Radio value="0" />
              </div>
              <div className="address-text">
                <h5 className="section-header">What you entered</h5>
                <p style={{ whiteSpace: "pre-line" }}>
                  {originalAddressReadable(modalData.originalAddress)}
                </p>
              </div>
            </div>

            <div
              onClick={() => setSelectedIdx("1")}
              className={`address-section${
                selectedIdx === "1" ? " selected" : ""
              }`}
            >
              <div>
                <Radio value="1" />
              </div>
              <div className="address-text">
                <h5 className="section-header">Recommended</h5>
                <p style={{ whiteSpace: "pre-line" }}>
                  {modalData &&
                    modalData.suggestedAddress &&
                    suggestedAddressReadable(modalData.suggestedAddress)}
                </p>
              </div>
            </div>
          </RadioGroup>

          <div className="button-container">
            <Button
              onClick={() => setShowModal({})}
              className="button back-button"
            >
              Back
            </Button>
            <Button
              onClick={() => {
                if (selectedIdx === "0") {
                  submit();
                } else {
                  submit({
                    address1: `${modalData.suggestedAddress.street_number} ${modalData.suggestedAddress.route}`,
                    address2: modalData.suggestedAddress.subpremise || "",
                    city: modalData.suggestedAddress.locality,
                    usState: usStatesList.find(
                      ({ abbreviation }) =>
                        abbreviation ===
                        modalData.suggestedAddress.administrative_area_level_1,
                    ),
                    zip: modalData.suggestedAddress.postal_code,
                  });
                }
              }}
              className="button"
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function MissingAddressComponentsModal({
  showModal,
  setShowModal,
  modalData = { originalAddress: {} },
  submit,
}) {
  const address2Ref = useRef(null);

  return (
    <Modal open={showModal}>
      <div className="address-modal-container">
        <div className="inner-modal-container">
          <h3>Missing Information</h3>
          <p style={{ marginBottom: "20px" }}>
            Does your building have multiple units?
          </p>

          <h5 className="section-header">What you entered</h5>
          <div className="address-section">
            <div className="address-text">
              <p style={{ whiteSpace: "pre-line" }}>
                {originalAddressReadable(modalData.originalAddress)}
              </p>
            </div>
          </div>

          <h5 className="section-header">Missing information</h5>

          <MuiTextField
            id="address2"
            variant="outlined"
            labeltext="Apt, suite, or unit"
            fullWidth
            inputRef={address2Ref}
            autoFocus
            autoComplete="off"
          />

          <p className="input-subtext">
            Leave blank if there are not multiple units
          </p>

          <div className="button-container">
            <Button
              onClick={() => setShowModal({})}
              className="button back-button"
            >
              Go back
            </Button>
            <Button
              onClick={() => {
                if (address2Ref.current.value) {
                  submit({
                    address2: address2Ref.current.value,
                  });
                } else {
                  submit();
                }
              }}
              className="button"
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function CouldntVerifyAddressModal({
  showModal,
  setShowModal,
  modalData = {},
  submit,
}) {
  const unconfirmedComponents = modalData.unconfirmedComponents || {};

  return (
    <Modal open={showModal}>
      <div className="address-modal-container">
        <div className="inner-modal-container">
          <h3>We couldn't verify your address</h3>
          <p style={{ marginBottom: "20px" }}>Review the verification issues</p>

          <h5 className="section-header">What you entered</h5>
          <div className="address-section">
            <div className="address-text">
              <p style={{ whiteSpace: "pre-line" }}>
                {originalAddressReadable(modalData.originalAddress)}
              </p>
            </div>
          </div>

          <h5 className="section-header">Verification issues</h5>

          {unconfirmedComponents.administrative_area_level_1 ? (
            <div className="couldnt-verify-section">
              <Warning />
              <div>
                <strong>Can't find this address in</strong>
                <p>{modalData.originalAddress.usState.name}</p>
              </div>
            </div>
          ) : unconfirmedComponents.route ? (
            <div className="couldnt-verify-section">
              <Warning />
              <div>
                <strong>Address not found</strong>
                <p>Please make sure you've entered address correctly</p>
              </div>
            </div>
          ) : unconfirmedComponents.street_number ? (
            <div className="couldnt-verify-section">
              <Warning />
              <div>
                <strong>Address number not found</strong>
                <p>{unconfirmedComponents.street_number}</p>
              </div>
            </div>
          ) : unconfirmedComponents.subpremise ? (
            <div className="couldnt-verify-section">
              <Warning />
              <div>
                <strong>Building unit not found</strong>
                <p>{unconfirmedComponents.subpremise}</p>
              </div>
            </div>
          ) : null}

          {unconfirmedComponents.locality && (
            <div className="couldnt-verify-section">
              <Warning />
              <div>
                <strong>
                  Can't confirm this city in{" "}
                  {modalData.originalAddress.usState.name}
                </strong>
                <p>{unconfirmedComponents.locality}</p>
              </div>
            </div>
          )}

          <div className="button-container">
            <Button onClick={() => submit({})} className="button back-button">
              Use entered
            </Button>
            <Button onClick={() => setShowModal({})} className="button">
              Make changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function POBoxShippingNotSupportedModal({ showModal, setShowModal }) {
  return (
    <Modal
      open={showModal}
      onClose={() => setShowModal({})}
      aria-labelledby="po-box-modal-title"
    >
      <div className="address-modal-container">
        <div className="inner-modal-container">
          <p style={{ fontSize: "18px", marginBottom: "20px" }}>
            FedEx Priority does not deliver to P.O. Boxes. Please select USPS
            Standard Shipping or provide a physical mailing address.
          </p>
          <div className="button-container">
            <Button
              color="primary"
              onClick={() => setShowModal({})}
              style={{ marginTop: "16px" }}
            >
              OK
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const priceOptionStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "12px 8px",
  color: kickNavy,
  borderTop: "2px solid #e0e0e0",
};

const priceTagLabelRightSideStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-end",
};

const radioStyle = {
  color: kickBlue,
  verticalAlign: "middle",
  marginRight: "8px",
  padding: 0,
};

const rightSideSubtextStyle = {
  color: "#444",
  fontSize: "13px",
  textAlign: "right",
};

const propsByOptionType = {
  [THREE_MONTH_RECURRING_OPTION]: {
    visitOnly: {
      pricePerMonth: PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
      pricePerDay: PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY_PER_DAY,
      orderType: ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
      optionName: "12-Week Sleep Plan Pharmacy Pickup",
      optionLine2: "(Pay in Full Discount)",
      detailText: "Save 20% with 3-month commitment",
    },
    mailOrder: {
      pricePerMonth: PRICE_SLEEP_MEMBERSHIP_QUARTERLY,
      pricePerDay: PRICE_SLEEP_MEMBERSHIP_QUARTERLY_PER_DAY,
      orderType: ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
      optionName: "12-Week Sleep Plan with All Medication Delivered",
      optionLine2: "(Pay in Full Discount)",
      detailText: "Save 22% with 3-month commitment",
    },
  },
  [ONE_MONTH_RECURRING_OPTION]: {
    visitOnly: {
      pricePerMonth: PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
      pricePerDay: PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY_PER_DAY,
      orderType: ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
      optionName: "12-Week Sleep Plan Pharmacy Pickup",
      optionLine2: "(Monthly Installments)",
      detailText: "No risk - cancel anytime",
    },
    mailOrder: {
      pricePerMonth: PRICE_SLEEP_MEMBERSHIP_MONTHLY,
      pricePerDay: PRICE_SLEEP_MEMBERSHIP_MONTHLY_PER_DAY,
      orderType: ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
      optionName: "12-Week Sleep Plan with All Medication Delivered",
      optionLine2: "(Monthly Installments)",
      detailText: "No risk - cancel anytime",
    },
  },
};

const SleepPriceOption = ({
  showConfirmation,
  setSleepVisitProductFromRadio,
  optionType,
  sleepProductType,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  let props = propsByOptionType[optionType];
  // Column (pickup vs mail) derives from the selected sleep order type so it always matches the
  // preselected radio, the address heading, and the order summary. Fulfillment is fixed upstream
  // by select-pharmacy; these radios only switch cadence within the chosen fulfillment column.
  props = isPharmacyPickupSleepOrderType(sleepProductType)
    ? props.visitOnly
    : props.mailOrder;

  return (
    <>
      <label style={priceOptionStyle}>
        {/* Left section */}
        <div style={{ display: "flex" }}>
          <Radio
            disabled={showConfirmation}
            style={{
              ...radioStyle,
              display: "inline-flex",
            }}
            size="medium"
            value={props.orderType}
            onChange={setSleepVisitProductFromRadio}
          />

          <div
            style={{
              alignItems: "center",
              display: "flex",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <strong
                style={{
                  fontSize: isMobile ? "14px" : "15px",
                  width: isMobile ? "170px" : "",
                  // marginRight: "30px",
                  marginLeft: "0",
                }}
              >
                {props.optionName}
                <br />
                <span style={{ fontWeight: "normal" }}>
                  {props.optionLine2}
                </span>
              </strong>
            </div>
          </div>
        </div>
        <div style={priceTagLabelRightSideStyle}>
          <div>
            <strong
              style={{
                fontSize: isMobile ? "15px" : "16px",

                marginRight: "10px",
              }}
            >
              ${props.pricePerMonth}
            </strong>
          </div>
        </div>
      </label>
      <div style={{ ...rightSideSubtextStyle, margin: "-4px 17px 10px" }}>
        ${props.pricePerDay}
        /night of great sleep.
        <br />
        <span style={{ color: kickRed, fontWeight: "bold" }}>
          {props.detailText}
        </span>
      </div>
    </>
  );
};

export const SleepSubscriptionSelectionSection = ({
  consultationType,
  showConfirmation,
  sleepProductType,
  setSleepVisitProductFromRadio,
  classes,
  checkoutSessionID,
}) => {
  // no sleep subscription selected yet - default to what's in state store
  if (!getVisitProduct(checkoutSessionID)) {
    setVisitProduct(sleepProductType, checkoutSessionID);
    setConsultationTypeOrderItem(
      consultationType,
      sleepProductType,
      checkoutSessionID,
    );
  }

  return (
    <React.Fragment>
      <h3 className={classes.subtitle} style={checkoutOptionHeadingStyle}>
        Choose Plan
      </h3>
      <div
        className={classes.paymentForm}
        style={{
          backgroundColor: kickLightBlue,
          border: "2px solid #e0e0e0",
          borderTop: "none",
          borderRadius: "4px",
          maxWidth: VISIT_MAX_WIDTH,
          marginTop: 0,
        }}
      >
        <RadioGroup
          style={{ width: "100%" }}
          aria-labelledby="demo-radio-buttons-group-label"
          defaultValue={sleepProductType}
          name="select-sleep-subscription-radio-buttons-group"
          value={sleepProductType}
        >
          {/* 3 Month Recurring Option */}
          <SleepPriceOption
            showConfirmation={showConfirmation}
            setSleepVisitProductFromRadio={setSleepVisitProductFromRadio}
            optionType={THREE_MONTH_RECURRING_OPTION}
            sleepProductType={sleepProductType}
          />
          {/* 1 Month Recurring Option */}
          <SleepPriceOption
            showConfirmation={showConfirmation}
            setSleepVisitProductFromRadio={setSleepVisitProductFromRadio}
            optionType={ONE_MONTH_RECURRING_OPTION}
            sleepProductType={sleepProductType}
          />
        </RadioGroup>
      </div>
    </React.Fragment>
  );
};

export function SelectedSleepMeds() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const stepNumberStyle = {
    width: "12px",
    display: "inline-block",
  };
  const sleepMedStyle = {
    fontWeight: "bold",
    marginTop: "6px",
    fontSize: isMobile ? "13px" : "14px",
  };

  const sleepMeds = getSelectedSleepMeds();

  if (!sleepMeds.length) return null;

  return (
    <div
      style={{
        backgroundColor: kickLightPink,
        border: "2px solid",
        borderColor: kickPink,
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        margin: "24px 0",
        padding: "16px",
        maxWidth: VISIT_MAX_WIDTH,
      }}
    >
      <div
        style={{
          color: kickBlue,
          marginBottom: "4px",
          fontWeight: "bold",
          fontSize: "15px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <LocalPharmacy
          style={{ fontSize: "18px", color: kickBlue, marginRight: "8px" }}
        />
        Sleep Medications You're Interested In
      </div>

      <ul style={{ paddingLeft: "16px", marginBottom: "0", marginTop: "0" }}>
        {sleepMeds.map((med) => (
          <li key={med} style={sleepMedStyle}>
            <span style={stepNumberStyle}>{med}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function WhatHappensNext() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const stepNumberStyle = {
    width: "12px",
    display: "inline-block",
  };
  const stepLineStyle = {
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "bold",
    marginTop: "6px",
    fontSize: isMobile ? "13px" : "14px",
  };
  const stepRightSideStyle = {
    color: kickBlue,
    textAlign: "right",
    paddingLeft: "10px",
  };
  return (
    <div
      style={{
        backgroundColor: kickLightBlue,
        borderColor: kickBlue,
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        margin: "24px 0",
        padding: "16px",
        maxWidth: VISIT_MAX_WIDTH,
      }}
    >
      <div
        style={{
          color: kickBlue,
          marginBottom: "4px",
          fontWeight: "bold",
          fontSize: "17px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <AccessTime
          style={{ fontSize: "17px", color: kickBlue, marginRight: "8px" }}
        />
        What Happens Next
      </div>
      <div style={stepLineStyle}>
        <div>
          <span style={stepNumberStyle}>1.</span> Doctor reviews your visit
        </div>
        <div style={stepRightSideStyle}>12 hours</div>
      </div>
      <div style={stepLineStyle}>
        <div>
          <span style={stepNumberStyle}>2.</span> Medication dosing trials at
          home
        </div>
        <div style={stepRightSideStyle}>2 to 3 weeks</div>
      </div>
      <div style={stepLineStyle}>
        <div>
          <span style={stepNumberStyle}>3.</span> Follow-Up visit and
          adjustments
        </div>
        <div style={stepRightSideStyle}>Week 3+</div>
      </div>
    </div>
  );
}

export function SleepConciergeInfo() {
  return (
    <div
      style={{
        backgroundColor: kickLightBlue,
        borderColor: kickBlue,
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        margin: "0 0 24px 0",
        padding: "16px",
        maxWidth: VISIT_MAX_WIDTH,
      }}
    >
      <div
        style={{
          color: kickBlue,
          marginBottom: "4px",
          fontWeight: "bold",
          fontSize: "17px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Phone
          style={{ fontSize: "17px", color: kickBlue, marginRight: "8px" }}
        />
        Free Sleep Concierge Call
      </div>
      <div style={{ fontSize: "14px", marginTop: "6px" }}>
        Get a free 15-minute call with a sleep concierge to go over your sleep
        plan right after checkout
      </div>
    </div>
  );
}
